import React, { createContext, useState, useEffect } from 'react';
import { User } from '../components/common/DataTypes';
import { AuthContext } from './AuthContext';
import { useTranslation } from 'react-i18next';

// Define the shape of the context value
interface UserContextType {
    currentUser: User;
    isComplete: boolean;
    isAdmin: boolean;
    completionRequest: string;
    updateUser: (updatedUser: User) => Promise<{ success: boolean; message: string }>;
}

const defaultProfileImageValue = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAOEAAADhCAMAAAAJbSJIAAAAPFBMVEXk5ueutLepsLPo6uursbXJzc/p6+zj5ea2u76orrKvtbi0ubzZ3N3O0dPAxcfg4uPMz9HU19i8wcPDx8qKXtGiAAAFTElEQVR4nO2d3XqzIAyAhUD916L3f6+f1m7tVvtNINFg8x5tZ32fQAIoMcsEQRAEQRAEQRAEQRAEQRAEQRAEQRAEQRAEQRAEQTghAJD1jWtnXJPP/54IgNzZQulSmxvTH6oYXX4WS+ivhTbqBa1r26cvCdCu6i0YXbdZ0o4A1rzV+5IcE3YE+z58T45lqo7g1Aa/JY5tgoqQF3qb382x7lNzBLcxft+O17QUYfQI4IIeklKsPSN4i6LKj/7Zm8n99RbHJpEw9gEBXNBpKIYLJqKYRwjOikf//r+J8ZsVuacbqCMNleI9TqGLGqMzhnVdBOdd6F/RlrFijiCoVMk320CBIahUxTWI0KKEcJqKbMdpdJb5QvdHq6wCI5qhKlgGMS/RBHkubWDAE+QZxB4xhCyDiDkLZxgGEVdQldzSKbTIhmZkFkSEPcVvmBn2SMuZB9od7fQDsMiDdKJjFUSCQarM5WirZ3C2TT/htYnyPcPfgrFHWz0BI74gr6J/IZiGUxAZGQLqmvQLTrtE/Go4YxhVRIpEw+sww1IIcqr5NKmUUzLF3d4/qPkYIp2T/obPuemlojFUR4t9Q2Vojhb7BmgElWHzLPH8hucfpefPNFTVgs9h1AdU/Pin96vwWbWdf+X9Absn3OdO34aMdsDnP8WgKYisTqI6CkNGqZQo1XA6Ef6AU32SJzOcBukHPF07/xNSgmHKa5BOhtezv6mA/rYJpwXNAnbRZ1XuF3BzDcO3vpA3+ny2909gbqE4hhD3LIPhLLyBNhPZvbZ3B+3tPYa18A7auSlXQayKwTPNLKDcuOB0xPYKDPFTkWsevQPRZ1J8Hji9I1KQ34r7hZhrwNwOZ97QxNx0drwn4QI0wQk1DcEsfKCWKdxVvxPSNUIp/knmAXT+nT+Ko3+0H96rcNb3m1fx7MBTJdeBJ7uFcWsc0wvgAsC4pROW0l2inbAmIBv/7GZmuhQH6API2rr8T0e6yuZJ+80A9LZeG62T3tik31XwxtwZcizKuTHkMjB1WdZde4Kmic/A5ZI3rr1ae21d08PlVHYfAaxw9G9CYRbJ+8ZdbTcMRV1XM3VdF0M32vtoTdZ0+u29s0OttJ5bz64UwinjaFMVY9vkqc3KKSxN21Xl+0L4Q3Vuv1tYl0pqnX6ms4XetFz7gdZVAgUEoJntfOUe4ZwsHd9FzqQ3Vv6xe41l0XJcqcKl6TZvlv7ClAW3BsqQW4X7ypApB8dmTgK4IX5wvqIVj33HtD2qSG4BqznxdIefL27Y4sahi0MdIdvUsDva8agGGbCtITmCY31MHD2O0uIdh/0rJDQ1VX5Zdxz3rR2QDbv6qXl9vudzqQtGm1Jv9LDXOsfvvB7VcZ8PDKD0mQ1VHPYQ9O+Yj4hR1IUD8rBnn3ho2m8oQMxbCFiKlL2ioSW5heeJqegED52CzxCtcGD3Kv8Wms9EYLyUhwaFIhSMBClevWEmiK/Iaogu4H7sg6ppQhQG8RUqivuTGOAJOg6FfgW0q0M0PQMRMEgXaeNf3SYDZ8PIMI0+wHgr/MgN7wYwpiLjCCqM6ydUDZLQiB6nDdNC8SDyig3jPPpFXGcC9O8BUBDVmgBY59E7Md/35Loe/UVEECEJwYggJjELZ4J71SaQSBeC02n4Da29CayJNA28SAhd2CQyC1Xw6pSmGSINQVuMhAZp4DClan9MgmkDDNmezqwS8sgtlXK/EPBhoaSmYVC/F7IO1jQEdHOlabpKh3+jzLQSTUiq4X2I+Ip/zU8rlaqAvkS21ElR+gqu3zbjjL+hIAiCIAiCIAiCIAiCsCf/AKrfVhSbvA+DAAAAAElFTkSuQmCC";
// Localized completion check
const testUserCompletion = (user: User, t?: (key: string) => string): string => {
    console.log('Testing user completion:', user);
    if (!user.email || !user.name || !user.image) {
        return t ? t('profile_incomplete.missing_fields') : "Nutzerprofil unvollständig. E-Mail, Name und Profilbild fehlen.";
    }
    if (user.image === "/profile-picture.png" || user.image === defaultProfileImageValue) {
        return t ? t('profile_incomplete.no_picture') : "Kein Profilbild gesetzt.";
    }
    return ""; // Empty string indicates the user is complete
};

// Create the context with a default value of `undefined`
export const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<any> = ({ children }) => {
    const authContext = React.useContext(AuthContext);
    const { t } = useTranslation();
    if (!authContext) {
        throw new Error('UserProvider must be used within an AuthProvider');
    }
    const { token } = authContext;

    const [currentUser, setCurrentUser] = useState<User>({
        id: 0,
        email: 'example@example.com',
        name: 'User not yet loaded',
        image: '/profile-picture.png',
        role: 'user',
        eventRole: 'participant',
        activeEvent: {
            id: 0,
            title: 'No active event',
            description: '',
            startDate: new Date().toISOString(),
            endDate: new Date().toISOString(),
            visible: true,
            codeRequired: false
        },
    });
    const [completionRequest, setCompletionRequest] = useState<string>("");

    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const response = await fetch('/api/users/me', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch user data');
                }

                const userData: User = await response.json();
                setCurrentUser(userData);
            } catch (error) {
                console.error('Error fetching current user:', error);
            }
        };

        fetchCurrentUser();
    }, [token]);

    useEffect(() => {
        setCompletionRequest(testUserCompletion(currentUser, t));
    }, [currentUser, t]);

    const isComplete = completionRequest.trim().length === 0;
    const isAdmin = currentUser.role === 'admin';

    const updateUser = async (updatedUser: User): Promise<{ success: boolean; message: string }> => {
        try {
            var data: any = updatedUser;
            if (data.email == currentUser.email) {
                delete data.email;
            }
            if (data.image === currentUser.image) {
                delete data.image;
            }
            if (data.name === currentUser.name) {
                delete data.name;
            }
            if (data.whatsapp === currentUser.whatsapp) {
                delete data.whatsapp;
            }
            if (data.instagram === currentUser.instagram) {
                delete data.instagram;
            }
            if (data.linkedin === currentUser.linkedin) {
                delete data.linkedin;
            }
            const response = await fetch('/api/users/me', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(data),
            });

            if (response.ok) {
                setCurrentUser(updatedUser);
                return { success: true, message: t('profile.save_success') };
            } else {
                const errorData = await response.json();
                return { success: false, message: errorData.message || t('profile.save_failed') };
            }
        } catch (error) {
            return { success: false, message: t('profile.save_error') };
        }
    };

    return (
        <UserContext.Provider value={{ currentUser, isComplete, completionRequest, isAdmin, updateUser }}>
            {children}
        </UserContext.Provider>
    );
};